<template>
  <v-card>
    <v-toolbar flat dark color="primary" dense>
      <v-toolbar-title class="font-weight-light">
        {{ vehicle.vehicleLabel }} &mdash; {{ tv('Live view') }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <v-card-text class="pa-3">
      <v-select
        v-model="channel"
        :items="channelItems"
        :label="tv('Channel')"
        :disabled="loading"
        outlined
        dense
        hide-details
        class="mb-3"
        @change="start"
      ></v-select>

      <div v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <div class="mt-2 caption">{{ tv('Connecting to camera') }}</div>
      </div>
      <div v-else-if="statusMessage" class="text-center py-8">
        <v-icon large color="grey">mdi-video-off-outline</v-icon>
        <div class="mt-2 caption">{{ statusMessage }}</div>
        <div v-if="statusDetail" class="caption grey--text">{{ statusDetail }}</div>
        <v-btn small outlined color="primary" class="mt-3" @click="start">
          {{ tv('Retry') }}
        </v-btn>
      </div>
      <video
        v-show="!loading && !statusMessage"
        ref="video"
        controls
        autoplay
        muted
        playsinline
        style="width: 100%; max-height: 400px; background: #000;"
      ></video>
    </v-card-text>
  </v-card>
</template>

<script>
import Hls from 'hls.js'
import httpClient from '@/api/httpClient'
import { createFlvPlayer, destroyFlvPlayer, isFlvSupported } from '@/utils/flvPlayer'
import translate from '@/utils/genTable/multilanguageHelper.js'

const DEVICE_OFFLINE = '201051'

export default {
  name: 'Camera360LiveViewDialog',
  props: {
    vehicle: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      statusMessage: null,
      statusDetail: null,
      hls: null,
      flv: null,
      session: null,
      channel: null,
      channelItems: []
    }
  },
  async mounted() {
    await this.loadChannels()
    if (this.channel !== null) {
      this.start()
    }
  },
  beforeDestroy() {
    this.stop()
  },
  methods: {
    tv(key) {
      return translate(key, 'pages.video360')
    },
    channelLabel(channel) {
      const types = channel.types.map(type => this.tv(type))
      return types.length ? `${channel.alias} - ${types.join(', ')}` : channel.alias
    },
    async loadChannels() {
      this.loading = true

      try {
        const response = await httpClient.get(
          `camera360/vehicles/${this.vehicle.vehicleId}/channels`
        )
        this.channelItems = response.data.map(c => ({
          text: this.channelLabel(c),
          value: c.number
        }))
        this.channel = this.channelItems.length ? this.channelItems[0].value : null
        if (this.channel === null) {
          this.statusMessage = this.tv('Live stream unavailable')
        }
      } catch (e) {
        const data = e.response && e.response.data
        this.statusMessage =
          (data && data.Message) || this.tv('Live stream unavailable')
      } finally {
        this.loading = false
      }
    },
    async start() {
      this.stop()
      this.loading = true
      this.statusMessage = null
      this.statusDetail = null

      try {
        // Streamax links are single-use and valid for 10 seconds, so a new one is requested on every start.
        const response = await httpClient.get(
          `camera360/vehicles/${this.vehicle.vehicleId}/live-link?channel=${this.channel}`
        )
        this.session = response.data.session
        this.play(response.data.format, response.data.url)
      } catch (e) {
        const data = e.response && e.response.data
        this.statusMessage =
          data && data.Code === DEVICE_OFFLINE
            ? this.tv('Camera offline')
            : (data && data.Message) || this.tv('Live stream unavailable')
      } finally {
        this.loading = false
      }
    },
    play(format, url) {
      const video = this.$refs.video

      if (!url) {
        this.statusMessage = this.tv('Live stream unavailable')
      } else if (format === 'flv') {
        this.playFlv(video, url)
      } else if (Hls.isSupported()) {
        this.hls = new Hls()
        this.hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            console.error('Streamax live view error', data)
            const code = data.response && data.response.code
            this.statusDetail = [data.type, data.details, code].filter(Boolean).join(' / ')
            this.statusMessage = this.tv('Live stream unavailable')
            this.stop()
          }
        })
        this.hls.loadSource(url)
        this.hls.attachMedia(video)
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = url
      } else {
        this.statusMessage = this.tv('Live stream unavailable')
      }
    },
    playFlv(video, url) {
      if (!isFlvSupported()) {
        this.statusMessage = this.tv('Live stream unavailable')
        return
      }

      this.flv = createFlvPlayer(video, url, (type, detail) => {
        console.error('Camera360 live view error', type, detail)
        this.statusDetail = [type, detail].filter(Boolean).join(' / ')
        this.statusMessage = this.tv('Live stream unavailable')
        this.stop()
      })
    },
    closeSession() {
      if (!this.session) {
        return
      }

      // Best effort: tells Streamax to stop streaming so the camera doesn't keep sending video.
      const session = this.session
      this.session = null
      httpClient
        .post(`camera360/vehicles/${this.vehicle.vehicleId}/live-link/close`, { session })
        .catch(() => {})
    },
    stop() {
      if (this.hls) {
        this.hls.destroy()
        this.hls = null
      }
      if (this.flv) {
        destroyFlvPlayer(this.flv)
        this.flv = null
      }
      this.closeSession()
      const video = this.$refs.video
      if (video) {
        video.removeAttribute('src')
        video.load()
      }
    }
  }
}
</script>
